import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { scrollToId } from "@/components/mobix/Navbar";

const EASE = [0.22, 1, 0.36, 1];

const SLIDES = ["/hero-1.jpeg", "/hero-2.jpeg", "/hero-3.jpeg", "/hero-4.jpeg"];

export default function Hero() {
  const { t } = useLang();
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setActive((p) => (p + 1) % SLIDES.length), 5500);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      id="hero"
      className="relative w-full overflow-hidden min-h-[100svh] flex items-center"
      data-testid="hero-section"
    >
      {/* Full-bleed background slider */}
      <div className="absolute inset-0 z-0 bg-mobix-navy">
        {SLIDES.map((src, i) => (
          <img
            key={src}
            src={src}
            alt="MOBIX"
            aria-hidden={i !== active}
            className={`absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-[1200ms] ease-out ${i === active ? "opacity-100" : "opacity-0"}`}
            data-testid={`hero-slide-${i}`}
          />
        ))}
        {/* Overlay for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-b md:bg-gradient-to-r from-mobix-navy/90 via-mobix-navy/60 to-mobix-navy/20" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-[1800px] mx-auto px-6 sm:px-8 md:px-12 lg:px-20 pt-[72px]">
        <div className="max-w-xl py-16 lg:py-24">
          <h1
            className="font-extrabold uppercase leading-[1.1] tracking-tight text-white text-[clamp(2.25rem,5vw,4.25rem)]"
            data-testid="hero-title"
          >
            {t.hero.lines.map((line, i) => (
              <span key={line + i} className="block overflow-hidden py-[0.12em]">
                <motion.span
                  className={`block ${i === 0 ? "text-mobix-orange" : "text-white"}`}
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, ease: EASE, delay: 0.3 + i * 0.13 }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <div className="mt-6 h-1 w-20 rounded-full bg-mobix-orange" />

          <p
            className="mt-6 max-w-md text-[clamp(1rem,1.5vw,1.25rem)] font-medium text-white/85 leading-relaxed"
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
            <button
              onClick={() => scrollToId("products")}
              data-testid="hero-cta-products"
              className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-3xl bg-mobix-orange text-white font-semibold shadow-lg shadow-mobix-orange/30 hover:scale-105 transition-transform duration-300"
            >
              {t.hero.ctaPrimary}
              <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
            </button>
            <button
              onClick={() => scrollToId("contact")}
              data-testid="hero-cta-contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-3xl bg-white/10 backdrop-blur-sm border-2 border-white text-white font-semibold hover:bg-white hover:text-mobix-navy transition-colors duration-300"
            >
              {t.hero.ctaSecondary}
            </button>
          </div>
        </div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex items-center justify-center gap-2.5">
        {SLIDES.map((src, i) => (
          <button
            key={src}
            onClick={() => setActive(i)}
            aria-label={`Slide ${i + 1}`}
            data-testid={`hero-dot-${i}`}
            className={`h-2 rounded-full transition-all duration-500 ${i === active ? "w-8 bg-mobix-orange" : "w-2 bg-white/50 hover:bg-white/80"}`}
          />
        ))}
      </div>
    </section>
  );
}
